import { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import api from '../api/client';

export default function TickerAutocomplete({ value, onChange, onSelect, onKeyDown, dropdownAlign, placeholder, className, style, inputStyle, inputClassName, disabled, autoFocus }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const blurTimer = useRef(null);
  const debounceTimer = useRef(null);
  const activeItemRef = useRef(null);
  const containerRef = useRef(null);
  const [dropdownMaxWidth, setDropdownMaxWidth] = useState(0);
  const listboxId = useId();

  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    // A right-aligned dropdown grows leftward from the container's right edge, so the
    // available room is measured from rect.right, not rect.left — using rect.left here
    // (correct for the default left-aligned case) let the dropdown claim more width than
    // actually fit and run off the left edge of the viewport whenever the container itself
    // sat near the right edge of a narrow screen (e.g. the mobile nav search).
    const available = dropdownAlign === 'right' ? rect.right - 8 : window.innerWidth - rect.left - 8;
    setDropdownMaxWidth(Math.min(340, available));
  }, [open, dropdownAlign]);

  useEffect(() => {
    clearTimeout(debounceTimer.current);
    if (!value || value.length < 1) { setSuggestions([]); setOpen(false); setSearchError(false); return; }
    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      setSearchError(false);
      try {
        const { data } = await api.get(`/tickers/search?q=${encodeURIComponent(value)}`);
        setSuggestions(data.tickers || []);
        setOpen((data.tickers || []).length > 0);
        setActiveIndex(-1);
      } catch {
        setSuggestions([]);
        setSearchError(true);
        setOpen(true);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceTimer.current);
  }, [value]);

  useEffect(() => {
    if (activeItemRef.current) activeItemRef.current.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function pick(ticker) {
    if (onSelect) {
      onSelect(ticker);
    } else {
      onChange(ticker.symbol);
    }
    setOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
  }

  function handleChange(e) {
    onChange(e.target.value.toUpperCase());
  }

  function handleBlur() {
    blurTimer.current = setTimeout(() => { setOpen(false); setActiveIndex(-1); }, 150);
  }

  function handleFocus() {
    clearTimeout(blurTimer.current);
    if (suggestions.length > 0) setOpen(true);
  }

  function handleKeyDown(e) {
    if (open) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, -1));
        return;
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        pick(suggestions[activeIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setActiveIndex(-1);
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  }

  const activeDescendant = open && activeIndex >= 0
    ? `ticker-option-${suggestions[activeIndex]?.symbol}`
    : undefined;

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }} className={className}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={activeDescendant}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        className={inputClassName}
        style={inputStyle}
      />
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          style={{
            position: 'absolute', top: '100%',
            ...(dropdownAlign === 'right' ? { right: 0 } : { left: 0 }),
            zIndex: 100,
            minWidth: Math.min(dropdownMaxWidth, 280),
            maxWidth: dropdownMaxWidth,
            background: 'var(--bg-surface)', border: '1px solid var(--navy-200)', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', margin: 0, padding: 0,
            listStyle: 'none', maxHeight: 280, overflowY: 'auto',
          }}>
          {loading && (
            <li style={{ padding: '0.5rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Searching…</li>
          )}
          {!loading && searchError && (
            <li style={{ padding: '0.5rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Search unavailable — try again</li>
          )}
          {!loading && !searchError && suggestions.map((t, i) => (
            <li
              key={t.symbol}
              id={`ticker-option-${t.symbol}`}
              role="option"
              aria-selected={i === activeIndex}
              ref={i === activeIndex ? activeItemRef : null}
              onMouseDown={() => pick(t)}
              style={{
                padding: '0.5rem 0.75rem', cursor: 'pointer', display: 'flex',
                gap: '0.5rem', alignItems: 'baseline', fontSize: '0.875rem',
                background: i === activeIndex ? 'var(--navy-100)' : '',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--navy-100)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = i === activeIndex ? 'var(--navy-100)' : ''; }}
            >
              <span style={{ fontWeight: 700, color: 'var(--text-primary)', minWidth: 60, flexShrink: 0 }}>{t.symbol}</span>
              <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
